// The app root (24.14): the only component besides tab and overlay roots that reads the store's
// signals. It applies the settings (theme, text size, motion) as data attributes, provides the
// explainer for <Num> tooltips, routes title / main by status, routes overlays by status and
// ui.overlay through the one overlay host, and owns the global keys: Enter ends the season from
// anywhere when allowed, Esc closes overlays (the shortage dialog owns its own Esc).
import { h } from 'preact';
import { useCallback, useEffect } from 'preact/hooks';
import type { Target } from '../sim/api';
import {
  cancelEndSeason,
  closeOverlay,
  continueGame,
  continueOnSavedConfig,
  deleteSlot,
  dismissError,
  dismissNotice,
  draft,
  endSeason,
  exportRun,
  forkOntoCurrentConfig,
  game,
  hoverPerson,
  importRun,
  loadAutosave,
  loadSlot,
  newGame,
  observerPause,
  observerPlay,
  observerStep,
  openOverlay,
  openPersonCard,
  openPrompt,
  openSaves,
  projection,
  requestEndSeason,
  runConfig,
  saves,
  selectPerson,
  setListFilter,
  setTab,
  settings,
  status,
  toTitle,
  ui,
} from '../store';
import {
  draftSummaryVM,
  headerVM,
  jobLabel,
  newGameFormVM,
  peopleListVM,
  projectionVM,
  reportVM,
  savesVM,
  stockBars,
  tooltipVM,
  warningsVM,
} from '../viewModels';
import { PersonCardRoot } from './people/person-card-root';
import { ReportOverlay } from './report/report-overlay';
import { HowToPlay } from './settings/how-to-play';
import { SettingsPanel } from './settings/settings-panel';
import { EndConfirm } from './shell/end-confirm';
import { ErrorBoundary } from './shell/error-boundary';
import { downloadFile } from './shell/download';
import { Header } from './shell/header';
import { Layout } from './shell/layout';
import { ErrorCard, NoticeBar, OlderBalanceNote, Problems } from './shell/notices';
import { OverlayHost } from './shell/overlay-host';
import { overlayComponents } from './shell/overlay-slots';
import { PeopleList } from './shell/people-list';
import { RightPanel } from './shell/right-panel';
import { SavesPanel } from './shell/saves-panel';
import { TabHost } from './shell/tab-host';
import { ExplainContext } from './tooltip/explain-context';
import { TitleScreen } from './title/title-screen';

const exportAndDownload = (shareable: boolean): void => {
  const file = exportRun({ shareable });
  if (file !== undefined) downloadFile(file);
};

const menuActions = {
  onSave: () => openSaves('save'),
  onLoad: () => openSaves('load'),
  onExport: exportAndDownload,
  onImport: (file: File) => void importRun(file),
  onSettings: () => openOverlay('settings'),
  onHowToPlay: () => openOverlay('howToPlay'),
  onLedger: () => openOverlay('ledger'),
  onTitle: () => toTitle(),
};

function Main() {
  const g = game.value;
  const d = draft.value;
  const st = status.value;
  const u = ui.value;
  const p = projection.value;
  if (g === null) return null;
  const cfg = runConfig();
  const header = headerVM(g, d, st, cfg, p.result?.seasons[0]?.fragility ?? null);
  return (
    <Layout
      header={
        <>
          <Header
            vm={header}
            observerRunning={u.observerRunning}
            canSave={!g.options.ironman}
            menu={menuActions}
            onEndSeason={requestEndSeason}
            onOpenPrompt={(id) => openPrompt(id)}
            onPlay={() => observerPlay()}
            onPause={observerPause}
            onStep={() => observerStep()}
          />
          {u.confirmingEnd && st === 'deciding' && (
            <EndConfirm summary={draftSummaryVM(g, d)} onConfirm={() => endSeason()} onCancel={cancelEndSeason} />
          )}
        </>
      }
      notices={
        <>
          {u.error !== null && <ErrorCard error={u.error} onDismiss={dismissError} />}
          <Problems problems={u.problems} />
          {u.notice !== null && <NoticeBar notice={u.notice} onDismiss={dismissNotice} />}
          {u.olderBalance !== null && u.overlay !== 'report' && (
            <OlderBalanceNote
              note={u.olderBalance}
              onKeep={continueOnSavedConfig}
              onFork={() => forkOntoCurrentConfig()}
            />
          )}
        </>
      }
      left={
        <PeopleList
          vm={peopleListVM(g, d, u.listFilter)}
          filter={u.listFilter}
          hovered={u.hoveredPersonId}
          selected={u.selectedPersonId}
          onFilter={setListFilter}
          onHover={hoverPerson}
          onOpen={openPersonCard}
        />
      }
      centre={<TabHost tab={u.tab} onTab={setTab} />}
      right={
        <RightPanel
          stocks={stockBars(g)}
          projection={projectionVM(g, p.result, p.pending, p.error)}
          warnings={warningsVM(g, d, p.result)}
        />
      }
    />
  );
}

function Title() {
  const u = ui.value;
  return (
    <TitleScreen
      form={newGameFormVM()}
      saves={savesVM(saves.value, null, '')}
      notice={u.notice}
      onDismissNotice={dismissNotice}
      onStart={(options, seed) => newGame(options, seed)}
      onContinue={() => continueGame()}
      onLoadSlot={(row) => loadSlot(row.id)}
      onDeleteSlot={(row) => deleteSlot(row.id)}
      onLoadAutosave={(row) => loadAutosave(row.id)}
      onImport={(file) => void importRun(file)}
      onSettings={() => openOverlay('settings')}
      onHowToPlay={() => openOverlay('howToPlay')}
    />
  );
}

/** Overlays are routed by status and ui.overlay through the one host (24.14). */
function Overlays() {
  const st = status.value;
  const u = ui.value;
  const g = game.value;
  // The shortage dialog is its own full-screen dialog (focus trap, Esc asks before discarding),
  // so it is not nested inside the host (M6-02).
  if (st === 'paused' && g !== null) return h(overlayComponents.shortage, {});
  switch (u.overlay) {
    case 'report': {
      const vm = g === null ? null : reportVM(g, draft.value?.eventChoices);
      if (vm === null) return null;
      return (
        <OverlayHost labelledBy="report-title" onClose={() => closeOverlay()} size="wide">
          {/* M7: a loaded save opens on its last report, which covered this choice. */}
          {u.olderBalance !== null && (
            <OlderBalanceNote
              note={u.olderBalance}
              onKeep={continueOnSavedConfig}
              onFork={() => forkOntoCurrentConfig()}
            />
          )}
          <ReportOverlay
            vm={vm}
            ended={st === 'ended'}
            jobLabel={jobLabel}
            onContinue={() => closeOverlay()}
            onAnswer={(id) => openPrompt(id)}
            onLedger={() => openOverlay('ledger')}
            onTitle={() => toTitle()}
          />
        </OverlayHost>
      );
    }
    case 'event':
      return (
        <OverlayHost label="Event" onClose={() => closeOverlay()}>
          {h(overlayComponents.event, {})}
        </OverlayHost>
      );
    case 'ledger':
      return (
        <OverlayHost label="Ledger" onClose={() => closeOverlay()} size="full">
          {h(overlayComponents.ledger, {})}
        </OverlayHost>
      );
    case 'settings':
      return (
        <OverlayHost labelledBy="settings-title" onClose={() => closeOverlay()}>
          <SettingsPanel />
        </OverlayHost>
      );
    case 'howToPlay':
      return (
        <OverlayHost labelledBy="howto-title" onClose={() => closeOverlay()}>
          <HowToPlay onClose={() => closeOverlay()} />
        </OverlayHost>
      );
    case 'person':
      return (
        <OverlayHost labelledBy="person-title" onClose={() => closeOverlay()} size="wide">
          <PersonCardRoot />
        </OverlayHost>
      );
    case 'saves':
      return (
        <OverlayHost labelledBy="saves-title" onClose={() => closeOverlay()} size="wide">
          <SavesPanel />
        </OverlayHost>
      );
    case 'shortage':
    case null:
      return null;
  }
}

const INTERACTIVE =
  'button, a[href], input, select, textarea, summary, [role="button"], [role="tab"], [contenteditable="true"], svg[tabindex], [tabindex]:not(main):not(body)';

function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(INTERACTIVE) !== null;
}

/** Enter ends the season from anywhere when allowed; Esc closes overlays (Section 22). */
function useGlobalKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const u = ui.peek();
      if (e.key === 'Enter') {
        const g = game.peek();
        if (g === null || isInteractive(e.target) || u.overlay !== null) return;
        if (!headerVM(g, draft.peek(), status.peek(), runConfig()).canEndSeason) return;
        e.preventDefault();
        requestEndSeason();
      } else if (e.key === 'Escape') {
        if (status.peek() === 'paused' || u.overlay === 'shortage') return;
        if (u.overlay !== null) closeOverlay();
        else if (u.confirmingEnd) cancelEndSeason();
        else if (u.selectedPersonId !== null) selectPerson(null);
        else return;
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

export function App() {
  const s = settings.value;
  useGlobalKeys();
  const explain = useCallback((target: Target) => {
    const g = game.peek();
    return g === null ? null : tooltipVM(g, target, runConfig());
  }, []);
  return (
    <div class="theme" data-theme={s.theme} data-text-size={s.textSize} data-motion={s.motion}>
      <ExplainContext.Provider value={explain}>
        <ErrorBoundary>
          {status.value === 'title' ? <Title /> : <Main />}
          <Overlays />
        </ErrorBoundary>
      </ExplainContext.Provider>
    </div>
  );
}
